import React, { useState, useEffect, useRef } from "react";
import { Search, Filter, MoreVertical, Edit, Trash2, Eye, Users, UserCheck, UserX, Trash, Loader2, AlertCircle, Plus, Minus, Coins } from "lucide-react";
import { useAdminStats } from "../../hooks/useAdminStats";
import { useUsers } from "../../hooks/useUsers";
import { USER_STATUS, SORT_OPTIONS, SORT_ORDER, EXPORT_FORMATS, BASE_URL } from "../../constant/constant";
import Pagination from "../Common/Pagination";
import TokenManagementModal from "./TokenManagementModal";
import BulkTokenModal from "./BulkTokenModal";
import { useDebounce } from "../../hooks/useDebounce";

const AdminUsers = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState(USER_STATUS.ALL);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.CREATED_AT);
  const [sortOrder, setSortOrder] = useState(SORT_ORDER.DESC);

  // Token management state
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [bulkTokenModalOpen, setBulkTokenModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tokenModalType, setTokenModalType] = useState('grant');
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Dropdown state for actions menu
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownRefs = useRef({});

  // Suspend confirmation modal state
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [userToSuspend, setUserToSuspend] = useState(null);
  const [isLoadingSuspend, setIsLoadingSuspend] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  // Fetch admin statistics
  const { stats, loading: statsLoading, error: statsError } = useAdminStats();

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Fetch users with pagination and filters
  const {
    users,
    pagination,
    loading: usersLoading,
    error: usersError,
    refetch,
    updateUser,
    deleteUser,
    exportUsers
  } = useUsers({
    page: currentPage,
    limit: 10,
    search: debouncedSearchTerm,
    status: filterStatus,
    sortBy,
    sortOrder
  });

  // Handle search resets page
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when searching
  }, [debouncedSearchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId) {
        const dropdownElement = dropdownRefs.current[openDropdownId];
        if (dropdownElement && !dropdownElement.contains(event.target)) {
          setOpenDropdownId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);

  // Remove suspended users from selected users when users list updates
  useEffect(() => {
    if (selectedUsers.length > 0) {
      const activeSelectedUsers = selectedUsers.filter(user => {
        // Find the current user data from the users list
        const currentUser = users.find(u => u._id === user._id);
        if (!currentUser) return false; // User no longer exists
        const normalizedStatus = currentUser.status?.toLowerCase();
        return normalizedStatus !== 'suspended';
      });

      if (activeSelectedUsers.length !== selectedUsers.length) {
        setSelectedUsers(activeSelectedUsers);
      }
    }
  }, [users, selectedUsers]);

  // Handle status filter change
  const handleStatusFilter = (newStatus) => {
    setFilterStatus(newStatus);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle user actions
  const handleUserAction = async (userId, action, userData = null) => {

    try {
      if (action === 'update' && userData) {
        const result = await updateUser(userId, userData);
        if (result.success) {
          // Show success message
          console.log('User updated successfully');
        }
      } else if (action === 'delete') {
        if (window.confirm('Are you sure you want to delete this user?')) {
          const result = await deleteUser(userId);
          if (result.success) {
            // Show success message
            console.log('User deleted successfully');
          }
        }
      }
    } catch (error) {
      console.error('Action failed:', error);
    }
  };

  // Handle export
  const handleExport = async (format = EXPORT_FORMATS.CSV) => {
    try {
      const result = await exportUsers(format);
      if (result.success) {
        console.log('Export started');
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Token management functions
  const handleTokenAction = (user, type) => {
    setSelectedUser(user);
    setTokenModalType(type);
    setTokenModalOpen(true);
  };

  const handleTokenSuccess = (response) => {
    console.log('Token operation successful:', response);
    // Refresh users data
    refetch();
  };

  const handleBulkTokenGrant = () => {
    if (selectedUsers.length === 0) {
      alert('Please select users first');
      return;
    }

    // Filter out suspended users
    const activeUsers = selectedUsers.filter(user => {
      const normalizedStatus = user.status?.toLowerCase();
      return normalizedStatus !== 'suspended';
    });

    if (activeUsers.length === 0) {
      alert('Cannot grant tokens to suspended users. Please select active users.');
      return;
    }

    // Update selected users to only include active users
    setSelectedUsers(activeUsers);
    setBulkTokenModalOpen(true);
  };

  const handleBulkTokenSuccess = (response) => {
    console.log('Bulk token grant successful:', response);
    // Refresh users data
    refetch();
    // Clear selected users
    setSelectedUsers([]);
  };

  const toggleUserSelection = (user) => {
    // Prevent selecting suspended users
    const normalizedStatus = user.status?.toLowerCase();
    if (normalizedStatus === 'suspended') {
      return;
    }

    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u._id === user._id);
      if (isSelected) {
        return prev.filter(u => u._id !== user._id);
      } else {
        return [...prev, user];
      }
    });
  };

  // Handle dropdown toggle
  const toggleDropdown = (userId) => {
    setOpenDropdownId(openDropdownId === userId ? null : userId);
  };

  // Handle suspend user action - show confirmation modal
  const handleSuspendUser = (user) => {
    setUserToSuspend(user);
    setSuspendModalOpen(true);
    setOpenDropdownId(null);
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
    setOpenDropdownId(null);
  };

  // Handle confirm suspend action
  const handleConfirmSuspend = async () => {
    if (!userToSuspend || isLoadingSuspend) return;

    setIsLoadingSuspend(true);

    try {
      const newStatus = userToSuspend.status === 'suspended' ? 'active' : 'suspended';
      const result = await updateUser(userToSuspend._id, { status: newStatus });
      if (result.success) {
        console.log(`User ${newStatus === 'suspended' ? 'suspended' : 'activated'} successfully`);
        refetch(); // Refresh the users list

        // Close modal and reset state
        setSuspendModalOpen(false);
        setUserToSuspend(null);
      }
    } catch (error) {
      console.error('Suspend user failed:', error);
    } finally {
      setIsLoadingSuspend(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete || isLoadingDelete) return;

    setIsLoadingDelete(true);

    try {
      const result = await deleteUser(userToDelete._id);
      if (result.success) {
        console.log('User deleted successfully');
        refetch();

        setDeleteModalOpen(false);
        setUserToDelete(null);
      }
    } catch (error) {
      console.error('Delete user failed:', error);
    } finally {
      setIsLoadingDelete(false);
    }
  };

  // Transform stats data for display
  const userStats = stats ? [
    {
      title: "Total Users",
      value: stats.totalUsers?.count?.toLocaleString() || "0",
      change: stats.totalUsers?.growthLabel || "No data",
      changeType: stats.totalUsers?.growth > 0 ? "positive" : "neutral",
      icon: Users,
      iconBg: "bg-green-100",
      iconColor: "text-green-600"
    },
    {
      title: "Active Users",
      value: stats.activeUsers?.count?.toLocaleString() || "0",
      change: stats.activeUsers?.growthLabel || "No data",
      changeType: stats.activeUsers?.growth > 0 ? "positive" : "neutral",
      icon: UserCheck,
      iconBg: "bg-green-100",
      iconColor: "text-green-600"
    },
    {
      title: "Suspended Users",
      value: stats.suspendedUsers?.count?.toLocaleString() || "0",
      change: stats.suspendedUsers?.pendingRequests ? `${stats.suspendedUsers.pendingRequests} pending requests` : "No data",
      changeType: "neutral",
      icon: UserX,
      iconBg: "bg-yellow-100",
      iconColor: "text-yellow-600"
    },
    {
      title: "Deleted Users",
      value: stats.deletedUsers?.count?.toLocaleString() || "0",
      change: stats.deletedUsers?.growthLabel || "No data",
      changeType: stats.deletedUsers?.growth > 0 ? "negative" : "neutral",
      icon: Trash,
      iconBg: "bg-red-100",
      iconColor: "text-red-600"
    }
  ] : [];

  return (
    <div className="min-h-screen p-6 space-y-6">
      {/* User Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsLoading ? (
          // Loading skeleton for stats
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
              </div>
            </div>
          ))
        ) : statsError ? (
          // Error state for stats
          <div className="col-span-full bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-red-600">Failed to load statistics: {statsError}</p>
            </div>
          </div>
        ) : (
          userStats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mb-2">{stat.value}</p>
                  <p className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' :
                    stat.changeType === 'negative' ? 'text-red-600' :
                      'text-gray-600'
                    }`}>
                    {stat.change}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${stat.iconBg}`}>
                  <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Users Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Users</h2>
          <div className="flex gap-2">
            <button
              onClick={() => handleExport(EXPORT_FORMATS.CSV)}
              className="bg-[#075038] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#075038]/90 transition-colors"
            >
              Export CSV
            </button>
            <button
              onClick={() => handleExport(EXPORT_FORMATS.JSON)}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors"
            >
              Export JSON
            </button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedUsers.length > 0 && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-blue-900">
                  {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={() => setSelectedUsers([])}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear selection
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleBulkTokenGrant}
                  className="flex items-center space-x-2 px-3 py-2 bg-[#075038] text-white text-sm font-medium rounded-lg hover:bg-[#075038]/90 transition-colors"
                >
                  <Coins className="w-4 h-4" />
                  <span>Grant Tokens</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search users by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#075038] focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => handleStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#075038] focus:border-transparent"
          >
            <option value={USER_STATUS.ALL}>All Status</option>
            <option value={USER_STATUS.ACTIVE}>Active</option>
            <option value={USER_STATUS.SUSPENDED}>Suspended</option>
            <option value={USER_STATUS.DELETED}>Deleted</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          {usersError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <p className="text-red-600">Failed to load users: {usersError}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : usersLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="h-16 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No users found</p>
              <p className="text-gray-400">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={(() => {
                        const activeUsers = users.filter(user => {
                          const normalizedStatus = user.status?.toLowerCase();
                          return normalizedStatus !== 'suspended';
                        });
                        return activeUsers.length > 0 && selectedUsers.length === activeUsers.length;
                      })()}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Only select non-suspended users
                          const activeUsers = users.filter(user => {
                            const normalizedStatus = user.status?.toLowerCase();
                            return normalizedStatus !== 'suspended';
                          });
                          setSelectedUsers(activeUsers);
                        } else {
                          setSelectedUsers([]);
                        }
                      }}
                      className="rounded border-gray-300 text-[#075038] focus:ring-[#075038]"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    API Calls
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user, index) => {
                  const normalizedStatus = user.status?.toLowerCase();
                  const isSuspended = normalizedStatus === 'suspended';
                  return (
                    <tr key={user._id} className={`hover:bg-gray-50 ${selectedUsers.some(u => u._id === user._id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedUsers.some(u => u._id === user._id)}
                          onChange={() => toggleUserSelection(user)}
                          disabled={isSuspended}
                          className={`rounded border-gray-300 text-[#075038] focus:ring-[#075038] ${isSuspended ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          title={isSuspended ? 'Cannot select suspended users for bulk token grant' : ''}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.no || (pagination.currentPage - 1) * pagination.limit + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {user.profilePicture?.url ? (
                            <img
                              src={user.profilePicture.url.includes("https://") ? user.profilePicture.url : BASE_URL + user.profilePicture.url}
                              alt={user.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                              <span className="text-white font-medium text-sm">
                                {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                              </span>
                            </div>
                          )}
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {user.tokens?.toLocaleString() || '0'}
                          </span>
                          {!isSuspended && (
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleTokenAction(user, 'grant')}
                                className="text-green-600 hover:text-green-900"
                                title="Grant Tokens"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleTokenAction(user, 'revoke')}
                                className="text-red-600 hover:text-red-900"
                                title="Revoke Tokens"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${user.plan?.includes('Starter') ? 'bg-yellow-100 text-yellow-800' :
                          user.plan?.includes('Pro') ? 'bg-orange-100 text-orange-800' :
                            user.plan?.includes('Advanced') ? 'bg-yellow-100 text-yellow-800' :
                              user.plan?.includes('Enterprise') ? 'bg-teal-100 text-teal-800' :
                                'bg-gray-100 text-gray-800'
                          }`}>
                          {user.plan || 'No Plan'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${normalizedStatus === 'active'
                          ? 'bg-green-100 text-green-800'
                          : isSuspended
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {user.status?.charAt(0).toUpperCase() + user.status?.slice(1) || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.apiCalls?.toLocaleString() || '0'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.lastActive || 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div
                          className="relative"
                          ref={(el) => {
                            if (el) {
                              dropdownRefs.current[user._id] = el;
                            }
                          }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(user._id);
                            }}
                            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                            title="Actions"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-600" />
                          </button>

                          {openDropdownId === user._id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                              <div className="py-1">
                                {!isSuspended && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTokenAction(user, 'grant');
                                      setOpenDropdownId(null);
                                    }}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    <div className="flex items-center space-x-2">
                                      <span>Grant Tokens</span>
                                    </div>
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSuspendUser(user);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-gray-100"
                                >
                                  <div className="flex items-center space-x-2">
                                    <span>{isSuspended ? 'Activate User' : 'Suspend User'}</span>
                                  </div>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteUser(user);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-gray-100"
                                >
                                  <div className="flex items-center space-x-2">
                                    <span>Delete User</span>
                                  </div>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination - Bottom Right */}
        <div className="mt-6 flex justify-end">
          <Pagination
            currentPage={pagination.currentPage || 1}
            totalPages={pagination.totalPages || 1}
            onPageChange={handlePageChange}
            hasNextPage={pagination.hasNextPage || false}
            hasPrevPage={pagination.hasPrevPage || false}
            totalItems={pagination.totalUsers || 0}
            itemsPerPage={pagination.limit || 10}
          />
        </div>
      </div>

      {/* Token Management Modals */}
      <TokenManagementModal
        isOpen={tokenModalOpen}
        onClose={() => setTokenModalOpen(false)}
        user={selectedUser}
        type={tokenModalType}
        onSuccess={handleTokenSuccess}
      />

      <BulkTokenModal
        isOpen={bulkTokenModalOpen}
        onClose={() => setBulkTokenModalOpen(false)}
        selectedUsers={selectedUsers}
        onSuccess={handleBulkTokenSuccess}
      />

      {/* Suspend User Confirmation Modal */}
      {suspendModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              {/* Warning Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                {userToSuspend?.status === 'suspended' ? 'Activate User' : 'Suspend User'}
              </h3>

              {/* Confirmation Question */}
              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you want to {userToSuspend?.status === 'suspended' ? 'activate' : 'suspend'} this user?
              </p>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={handleConfirmSuspend}
                  disabled={isLoadingSuspend}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${isLoadingSuspend
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : userToSuspend?.status === 'suspended'
                      ? 'bg-[#075038] text-white hover:bg-[#075038]/90'
                      : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                >
                  {isLoadingSuspend && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>
                    {isLoadingSuspend
                      ? 'Processing...'
                      : userToSuspend?.status === 'suspended' ? 'Activate' : 'Suspend'
                    }
                  </span>
                </button>
                <button
                  onClick={() => {
                    setSuspendModalOpen(false);
                    setUserToSuspend(null);
                    setOpenDropdownId(null);
                    setIsLoadingSuspend(false);
                  }}
                  disabled={isLoadingSuspend}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium border transition-colors ${isLoadingSuspend
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete User
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                This action cannot be undone. Are you sure you want to delete this user?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleConfirmDelete}
                  disabled={isLoadingDelete}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${isLoadingDelete
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                >
                  {isLoadingDelete && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{isLoadingDelete ? 'Deleting...' : 'Delete'}</span>
                </button>
                <button
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setUserToDelete(null);
                    setOpenDropdownId(null);
                    setIsLoadingDelete(false);
                  }}
                  disabled={isLoadingDelete}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium border transition-colors ${isLoadingDelete
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
